import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../auth/api';
import { useAuth } from '../../auth/AuthContext';
import SkyDecor from '../../components/SkyDecor';
import BlockExit from '../CapturePage/BlockExit';
import { enqueueRecording } from '../../services/offlineQueue';
import { syncNow } from '../../services/syncEngine';
import './ChildPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';



export default function ChildPage() {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state || {};

  // Case History capture mode. The same page, recorded into a session: the URL
  // carries it so a reload in the middle of a sitting does not silently turn
  // assessment recordings back into practice ones.
  const [searchParams] = useSearchParams();
  const captureSessionId = searchParams.get('capture')
    ? Number(searchParams.get('capture')) : null;
  const captureChildId = searchParams.get('child') ? Number(searchParams.get('child')) : null;
  const inCapture = Boolean(captureSessionId);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [userSpeech, setUserSpeech] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [azureStatus, setAzureStatus] = useState('checking');
  const [error, setError] = useState('');
  const [lastAudioUrl, setLastAudioUrl] = useState(null);
  const [savedOfflineCount, setSavedOfflineCount] = useState(0);
  const [offlineSaved, setOfflineSaved] = useState(null); // { cheer, count, word } for the offline feedback box
  const [showCelebration, setShowCelebration] = useState(false);
  const [mascotState, setMascotState] = useState('idle'); // idle, listening, processing, success, error
  const [sessionDetails, setSessionDetails] = useState([]); // Array to store per-word results
  const [xpScore, setXpScore] = useState(0);
  const [xpPop, setXpPop] = useState(false);
  const [streak, setStreak] = useState(0);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [cardEffect, setCardEffect] = useState(''); // '' | 'card-correct' | 'card-error'
  const [mascotSpeech, setMascotSpeech] = useState('');

  // Refs for media recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const isRecordingRef = useRef(false);

  useEffect(() => {
    fetchChildren();
    checkAzureStatus();
    // Re-check connectivity periodically and whenever the browser toggles
    // online/offline, so azureStatus reflects real Azure reachability. This
    // lets a Speak click route straight to the offline queue instead of
    // hanging ~3.5s on a doomed recognition call. See OFFLINE_RECORDING_PLAN.md.
    const healthInterval = setInterval(checkAzureStatus, 15000);
    window.addEventListener('online', checkAzureStatus);
    window.addEventListener('offline', checkAzureStatus);
    // Initialize AudioContext
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

    return () => {
      clearInterval(healthInterval);
      window.removeEventListener('online', checkAzureStatus);
      window.removeEventListener('offline', checkAzureStatus);
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Auto-select child if coming from ExerciseHub with state
  useEffect(() => {
    const wanted = captureChildId || routeState.childId;
    if (wanted && children.length > 0 && !selectedChild) {
      handleChildSelect(wanted);
    }
  }, [routeState.childId, captureChildId, children]);

  const fetchChildren = async () => {
    try {
      const response = await api.get(`/children/`);
      setChildren(response.data);
    } catch (error) {
      console.error('Error fetching children:', error);
      setError('Failed to load children data');
    }
  };

  const checkAzureStatus = async () => {
    try {
      const response = await api.get(`/health`);
      setAzureStatus(response.data.status);
    } catch (error) {
      console.error('Error checking Azure status:', error);
      setAzureStatus('error');
    }
  };

  const getMascot = () => {
    switch (mascotState) {
      case 'listening': return '👂';
      case 'processing': return '🤔';
      case 'success': return '🎉';
      case 'error': return '😕';
      default: return '🙂';
    }
  };

  const spawnConfetti = () => {
    const pieces = Array.from({ length: 35 }, (_, i) => ({
      id: Date.now() + i,
      left: Math.random() * 100,
      color: ['#FFD700', '#FF6B6B', '#4DD0E1', '#CE93D8', '#AED581', '#FF8A65', '#81D4FA'][Math.floor(Math.random() * 7)],
      delay: Math.random() * 0.5,
      duration: 1.2 + Math.random() * 1.5,
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360
    }));
    setConfettiPieces(pieces);
    setTimeout(() => setConfettiPieces([]), 3000);
  };

  const spawnFloatingEmoji = (emoji) => {
    const id = Date.now();
    const left = 30 + Math.random() * 40;
    setFloatingEmojis(prev => [...prev, { id, emoji, left }]);
    setTimeout(() => setFloatingEmojis(prev => prev.filter(e => e.id !== id)), 1600);
  };

  const showMascotSpeech = (text) => {
    setMascotSpeech(text);
    setTimeout(() => setMascotSpeech(''), 2500);
  };

  const playTone = (frequency, type, duration) => {
    if (!audioContextRef.current) return;
    const osc = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);

    gainNode.gain.setValueAtTime(0.1, audioContextRef.current.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration);

    osc.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);

    osc.start();
    osc.stop(audioContextRef.current.currentTime + duration);
  };

  const playSuccessSound = () => {
    playTone(600, 'sine', 0.1);
    setTimeout(() => playTone(800, 'sine', 0.2), 100);
  };

  const playErrorSound = () => {
    playTone(300, 'sawtooth', 0.3);
    setTimeout(() => playTone(200, 'sawtooth', 0.4), 150);
  };

  const handleChildSelect = async (childId) => {
    const child = children.find(c => c.id === childId);
    setSelectedChild(child);
    setError('');

    try {
      const response = await api.get(`/assignments/${childId}`);
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError('Failed to load assignments');
    }
  };

  const startAssignment = async (assignment) => {
    setCurrentAssignment(assignment);
    setCurrentExerciseIndex(0);
    resetSessionState();
    setXpScore(0);
    setStreak(0);
    setCardEffect('');

    const exerciseIds = JSON.parse(assignment.exercise_ids);
    if (exerciseIds.length > 0) {
      loadExercise(exerciseIds[0]);
    }
  };

  const resetSessionState = () => {
    setUserSpeech('');
    setEvaluation(null);
    setError('');
    setLastAudioUrl(null);
    setOfflineSaved(null);
  };

  // Clear details when starting new assignment
  useEffect(() => {
    if (currentAssignment) {
      setSessionDetails([]);
    }
  }, [currentAssignment]);

  const loadExercise = async (id) => {
    try {
      const response = await api.get(`/exercises/${id}`);
      setCurrentExercise(response.data);
    } catch (error) {
      console.error('Error fetching exercise:', error);
      setError('Failed to load exercise');
    }
  };

  const handleNextExercise = () => {
    if (!currentAssignment) return;
    const exerciseIds = JSON.parse(currentAssignment.exercise_ids);
    const nextIndex = currentExerciseIndex + 1;

    if (nextIndex < exerciseIds.length) {
      setCurrentExerciseIndex(nextIndex);
      resetSessionState();
      loadExercise(exerciseIds[nextIndex]);
    } else {
      // Session Complete - Calculate and Submit stats
      completeSession();
      playSuccessSound();
      setShowCelebration(true);
      setMascotState('success');
    }
  };

  const completeSession = async () => {
    if (!currentAssignment) return;

    const completedCount = sessionDetails.length;
    const correctCount = sessionDetails.filter(d => d.error_type === 'correct').length;
    const totalScore = sessionDetails.reduce((sum, d) => sum + (d.score || 0), 0);
    const avgScore = completedCount > 0 ? (totalScore / completedCount) : 0;

    // Stars logic: 1 star per correct answer
    const stars = correctCount;

    try {
      await api.post(`/session/complete`, {
        child_id: selectedChild.id,
        assignment_id: currentAssignment.id,
        total_exercises: JSON.parse(currentAssignment.exercise_ids).length,
        completed_exercises: completedCount,
        correct_count: correctCount,
        average_score: avgScore,
        stars_earned: stars,
        details: sessionDetails
      });
      console.log("Session saved successfully");
    } catch (err) {
      console.error("Failed to save session:", err);
    }
  };

  const closeCelebration = () => {
    setShowCelebration(false);
    setCurrentAssignment(null);
    setCurrentExercise(null);
    setCurrentExerciseIndex(0);
    setMascotState('idle');
  };

  const handlePreviousExercise = () => {
    if (!currentAssignment || currentExerciseIndex <= 0) return;
    const exerciseIds = JSON.parse(currentAssignment.exercise_ids);
    const prevIndex = currentExerciseIndex - 1;

    setCurrentExerciseIndex(prevIndex);
    resetSessionState();
    loadExercise(exerciseIds[prevIndex]);
  };

  const startRecording = async () => {
    try {
      resetSessionState();
      setMascotState('listening');

      // Recording is always allowed. If Azure is reachable we evaluate live;
      // otherwise the recording is queued and synced later, so there is no
      // reason to block the child here (see OFFLINE_RECORDING_PLAN.md).

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true }
      });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        // Clear the timer if it's still running
        if (recordingTimerRef.current) {
          clearTimeout(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        // Update ref to indicate not recording anymore
        isRecordingRef.current = false;

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setLastAudioUrl(audioUrl);
        await processAudioRecording(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      isRecordingRef.current = true; // Update ref

      // Set up auto-stop timer for 8 seconds
      recordingTimerRef.current = setTimeout(() => {
        // Check if still recording before stopping
        if (isRecordingRef.current) {
          stopRecording();
        }
      }, 8000); // 8 seconds
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Need microphone permission to hear you!');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecordingRef.current) {
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }

    // Clear the timer if it's still running
    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setIsRecording(false);
    isRecordingRef.current = false; // Update ref
  };

  // Build an offline queue item from the current exercise + child context.
  const buildQueueItem = (base64Audio) => ({
    id:
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    child_id: selectedChild.id,
    exercise_id: currentExercise.id,
    audio_data: base64Audio,
    reference_text: currentExercise.word_text || currentExercise.hindi_word,
    hindi_word: currentExercise.hindi_word,
    language: currentExercise.language || 'hi-IN',
    // Null for practice. A queued capture recording must still be a capture
    // recording when it syncs tomorrow.
    capture_session_id: captureSessionId,
    client_recorded_at: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
  });

  // Encouraging feedback for an offline-saved recording. Deliberately gives a
  // fun, gamified "good effort" vibe WITHOUT any score or correctness claim —
  // the real evaluation happens on the server after sync. See OFFLINE_RECORDING_PLAN.md.
  const showOfflineSavedFeedback = () => {
    setUserSpeech('');
    setEvaluation(null);
    setError('');
    setCardEffect('');
    setMascotState('success');
    const cheers = ['Great try!', 'Well done!', 'Awesome effort!', 'Super!'];
    const cheer = cheers[Math.floor(Math.random() * cheers.length)];
    const word = currentExercise?.word_text || currentExercise?.hindi_word || currentExercise?.word || '';
    setSavedOfflineCount((prev) => {
      const next = prev + 1;
      setOfflineSaved({ cheer, count: next, word });
      showMascotSpeech(`${cheer} 🌟 Saved! 📦 (${next} done)`);
      return next;
    });
    try { playSuccessSound(); } catch (e) { /* audio ctx may be unavailable */ }
    spawnFloatingEmoji('⭐');
  };

  const processAudioRecording = async (audioBlob) => {
    setIsProcessing(true);
    setMascotState('processing');
    setError('');
    setOfflineSaved(null);

    try {
      const base64Audio = await blobToBase64(audioBlob);

      // ---- OFFLINE / slow-network path: queue it, no instant score ----
      const isOnline = navigator.onLine && azureStatus === 'healthy';
      if (!isOnline && currentExercise && selectedChild) {
        await enqueueRecording(buildQueueItem(base64Audio));
        showOfflineSavedFeedback();
        syncNow(); // opportunistic: try immediately in case network is flaky-up
        return;
      }

      // Pronunciation Evaluation. This single call also returns the recognized
      // text (user_speech), so we no longer make a separate /speech-to-text/
      // request — that doubled the Azure round-trips and, when offline, showed
      // a raw connectivity error before this call could queue the recording.
      if (currentExercise) {
        const submission = {
          child_id: selectedChild.id,
          exercise_id: currentExercise.id,
          audio_data: base64Audio,
          reference_text: currentExercise.word_text || currentExercise.hindi_word,
          hindi_word: currentExercise.hindi_word,
          language: currentExercise.language || 'hi-IN',
          capture_session_id: captureSessionId
        };

        // Bound the wait: if the network is silently dead (browser still
        // reports "online" but nothing reaches the server), abort after 8s and
        // fall through to the offline queue instead of hanging the Speak button.
        const evaluationResponse = await api.post(`/evaluate-pronunciation/`, submission, { timeout: 8000 });
        const evalData = evaluationResponse.data;
        setEvaluation(evalData);
        setUserSpeech(evalData.user_speech || "???");

        // Track result
        setSessionDetails(prev => [
          ...prev,
          {
            word: currentExercise.hindi_word,
            english_word: currentExercise.word,
            user_speech: evalData.user_speech || "???",
            score: evalData.accuracy_score || 0,
            error_type: evalData.error_type || "unknown"
          }
        ]);

        if (inCapture) {
          // Same warmth either way: the mascot must not react to an error.
          playSuccessSound();
          setMascotState('success');
          spawnFloatingEmoji('⭐');
          showMascotSpeech('Nice one!');
        } else if (evalData.error_type === 'correct') {
          playSuccessSound();
          setMascotState('success');
          setCardEffect('card-correct');
          setXpScore(prev => prev + 10);
          setXpPop(true);
          setTimeout(() => setXpPop(false), 600);
          setStreak(prev => prev + 1);
          spawnConfetti();
          spawnFloatingEmoji('🌟');
          const phrases = ['Amazing!', 'Super!', 'Perfect!', 'Wow!'];
          showMascotSpeech(phrases[Math.floor(Math.random() * phrases.length)]);
        } else {
          playErrorSound();
          setMascotState('error');
          setCardEffect('card-error');
          setStreak(0);
          showMascotSpeech('Try again! 💪');
        }
        setTimeout(() => setCardEffect(''), 1000);
      }
    } catch (error) {
      console.error('Error processing audio:', error);
      // Network/server failure during an online attempt → queue it so the
      // recording is never lost; it will sync when the connection recovers.
      try {
        if (currentExercise && selectedChild) {
          const base64Audio = await blobToBase64(audioBlob);
          await enqueueRecording(buildQueueItem(base64Audio));
          showOfflineSavedFeedback();
          // The live attempt failed → treat the service as unreachable so the
          // NEXT recording skips the doomed call and queues instantly.
          setAzureStatus('error');
          checkAzureStatus(); // re-probe in the background to recover when online
          return;
        }
      } catch (queueErr) {
        console.error('Failed to queue recording offline:', queueErr);
      }
      setError('Oops! Something went wrong. Try again?');
      setMascotState('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const speakWord = async (word, language = 'hi-IN') => {
    try {
      const response = await api.post(`/speak-word/`, { text: word, language: language });
      if (response.data.status === 'success' && response.data.audio_data) {
        const audio = new Audio(`data:audio/wav;base64,${response.data.audio_data}`);
        audio.play();
      } else {
        throw new Error(response.data.message || 'Failed to get audio from server');
      }
    } catch (error) {
      console.warn('Backend TTS failed, falling back to browser synthesis:', error);
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(word);
        utterance.lang = language;
        speechSynthesis.speak(utterance);
      }
    }
  };

  const replayUserAudio = () => {
    if (lastAudioUrl) {
      const audio = new Audio(lastAudioUrl);
      audio.play();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // --- Components ---

  const Navbar = () => (
    <div className="child-navbar">
      {/* In a capture session there is deliberately no way out of this screen:
          no Exercise Hub, no Logout. A child who wanders finds their own word
          list and nothing else. The examiner ends the block with a long press. */}
      {inCapture ? (
        <>
          <span className="nav-home-btn" style={{ visibility: 'hidden' }}>—</span>
          <div className="nav-title">{selectedChild.name} · let's play</div>
          <BlockExit onExit={() => navigate(`/intake/capture/${captureSessionId}`)} label="Hold to stop" />
        </>
      ) : (
        <>
          <button className="nav-home-btn" onClick={() => navigate('/child/hub')}>
            ← Exercise Hub
          </button>
          <div className="nav-title">Hey {selectedChild.name}, let's play</div>
          <button onClick={logout} style={{ padding: '6px 14px', background: '#f44336', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem' }}>Logout</button>
        </>
      )}
    </div>
  );

  if (!selectedChild) {
    // If no child selected and no route state, redirect to hub
    return (
      <div className="child-page-container theme-selection sb-page">
        <SkyDecor />
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 20px' }}>
          <button onClick={() => navigate('/child/hub')} style={{ padding: '6px 14px', background: '#2196F3', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem', marginRight: '10px' }}>← Back to Hub</button>
          <button onClick={logout} style={{ padding: '6px 14px', background: '#f44336', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem' }}>Logout</button>
        </div>
        <h1>👋 Select Child to start exercise</h1>
        <div className="child-grid">
          {children.map(child => (
            <button key={child.id} onClick={() => handleChildSelect(child.id)} className="child-card-btn">
              <div className="avatar">👤</div>
              <div className="name">{child.name}</div>
            </button>
          ))}
        </div>
        {azureStatus !== 'healthy' && (
          <div className="status-badge">📴 Offline mode — recordings will be saved and synced later</div>
        )}
      </div>
    );
  }

  if (!currentExercise) {
    return (
      <div className="child-page-container theme-dashboard sb-page">
        <SkyDecor />
        <Navbar />
        <div className="dashboard-content">
          <h1>Welcome, {selectedChild.name}! 🌟</h1>
          <p>Choose your mission:</p>
          <div className="assignments-grid">
            {assignments.length > 0 ? [...assignments]
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map(assignment => (
              <div key={assignment.id} className="mission-card" onClick={() => startAssignment(assignment)}>
                <div className="mission-badge">Mission #{assignment.id}</div>
                <div className="icon">🚀</div>
                <h3>Start Adventure</h3>
                <div className="mission-info">
                  <p className="word-count">📚 {JSON.parse(assignment.exercise_ids).length} Words</p>
                  <p className="mission-date">📅 {formatDate(assignment.created_at)}</p>
                  <p className="mission-from">From: {assignment.assigned_by}</p>
                </div>
              </div>
            )) : (
              <div className="empty-state">No missions yet! Ask your teacher.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const exerciseIds = currentAssignment ? JSON.parse(currentAssignment.exercise_ids) : [];

  // Star Progress Component
  const StarProgress = () => (
    <div className="star-progress">
      {exerciseIds.map((id, index) => (
        <div key={index} className={`star ${index < currentExerciseIndex ? 'active' : ''}`}>
          {index < currentExerciseIndex ? '⭐' : '☆'}
        </div>
      ))}
    </div>
  );

  const correctCount = sessionDetails.filter(d => d.error_type === 'correct').length;
  const level = Math.floor(xpScore / 50) + 1;
  const xpInLevel = xpScore % 50;

  // Generate celebration confetti
  const celebConfetti = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    color: ['#FFD700', '#FF6B6B', '#4DD0E1', '#CE93D8', '#AED581', '#FF8A65'][Math.floor(Math.random() * 6)],
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 3,
    size: 6 + Math.random() * 10
  }));

  return (
    <div className="child-page-container theme-exercise sb-page">
      <SkyDecor />
      {/* Confetti Particles */}
      {confettiPieces.length > 0 && (
        <div className="art-confetti-container">
          {confettiPieces.map(p => (
            <div
              key={p.id}
              className="art-confetti-piece"
              style={{
                left: `${p.left}%`,
                backgroundColor: p.color,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                transform: `rotate(${p.rotation}deg)`
              }}
            />
          ))}
        </div>
      )}

      {showCelebration && (
        <div className="celebration-overlay">
          {/* Celebration confetti rain */}
          <div className="celebration-confetti-bg">
            {celebConfetti.map(p => (
              <div
                key={p.id}
                className="celebration-confetti-piece"
                style={{
                  left: `${p.left}%`,
                  backgroundColor: p.color,
                  animationDelay: `${p.delay}s`,
                  animationDuration: `${p.duration}s`,
                  width: `${p.size}px`,
                  height: `${p.size}px`
                }}
              />
            ))}
          </div>
          <div className="celebration-content">
            <div className="trophy">🏆</div>
            <h1>Mission Accomplished!</h1>

            <p>You are a Super Star!</p>
            <button onClick={closeCelebration}>Play More 🎮</button>
          </div>
        </div>
      )}

      <Navbar />

      {/* Streak Bar (XP and Level removed) */}
      <div className="game-stats-bar" style={{ justifyContent: 'center' }}>
        {streak >= 2 && (
          <div className="streak-badge">
            🔥 {streak} streak!
          </div>
        )}
      </div>

      <div className="game-progress">
        <div className="mascot-wrap">
          <div className="mascot-container" onClick={() => speakWord(currentExercise?.hindi_word, currentExercise?.language)}>
            {getMascot()}
          </div>
          {mascotSpeech && <div className="mascot-speech">{mascotSpeech}</div>}
        </div>
        <StarProgress />
      </div>

      <div className="game-stage">
        {/* Navigation - Left */}
        <button
          className="nav-arrow prev"
          onClick={handlePreviousExercise}
          disabled={currentExerciseIndex === 0}
        >
          ⬅️
        </button>

        {/* Main Card */}
        <div className={`exercise-card-main ${cardEffect}`}>
          {error && <div className="game-alert">{error}</div>}

          <div className="image-stage">
            {currentExercise.image_url ? (
              <img
                src={currentExercise.image_url.startsWith('http')
                  ? currentExercise.image_url
                  : `${BACKEND_URL}${currentExercise.image_url}`}
                alt="word"
                className="main-image"
              />
            ) : (
              <div className="placeholder-image">{currentExercise.hindi_word.charAt(0)}</div>
            )}
          </div>

          <h2 className="word-target">{currentExercise.hindi_word}</h2>

          <div className="controls-row">
            <button onClick={() => speakWord(currentExercise.hindi_word, currentExercise.language)} className="action-btn sound-btn">
              🔊 Listen
            </button>

            <button
              onClick={isRecordingRef.current ? stopRecording : startRecording}
              className={`action-btn mic-btn ${isRecordingRef.current ? 'recording' : ''}`}
              disabled={isProcessing}
            >
              {isProcessing ? '⏳' : isRecordingRef.current ? '⏹ Stop' : '🎤 Speak'}
            </button>
          </div>

          {/* Floating Emojis */}
          {floatingEmojis.map(e => (
            <div key={e.id} className="floating-emoji" style={{ left: `${e.left}%`, top: '30%' }}>
              {e.emoji}
            </div>
          ))}

          {/* Feedback Section */}
          {/* During an assessment the child is told nothing about how they did.
              Corrective feedback would change the next word they say, and the
              recording is meant to show how they speak, not how they respond to
              being corrected. Stars and warmth stay; right and wrong go. */}
          {inCapture && (evaluation || userSpeech || offlineSaved) ? (
            <div className="feedback-bubble success">
              <div className="feedback-icon">⭐ Great job!</div>
              <div className="feedback-tip">Let's try the next one.</div>
              {lastAudioUrl && (
                <button onClick={replayUserAudio} className="replay-btn">
                  🔁 Listen to Yourself
                </button>
              )}
            </div>
          ) : null}

          {!inCapture && (evaluation || userSpeech || offlineSaved) && (
            <div className={`feedback-bubble ${evaluation?.error_type === 'correct' ? 'success' : 'pending'}`}>
              {offlineSaved ? (
                <>
                  <div className="feedback-icon">⭐ {offlineSaved.cheer}</div>
                  <div className="feedback-message" style={{
                    marginTop: '10px',
                    fontSize: '1.2rem',
                    color: '#90CAF9',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    padding: '8px 15px',
                    borderRadius: '15px'
                  }}>
                    Recording saved! 📦 We'll check it when you're back online.
                  </div>
                  {offlineSaved.word && (
                    <div className="feedback-tip">You practiced: "{offlineSaved.word}"</div>
                  )}
                  <div className="feedback-tip">📦 {offlineSaved.count} saved this session</div>
                  {lastAudioUrl && (
                    <button onClick={replayUserAudio} className="replay-btn">
                      🔁 Listen to Yourself
                    </button>
                  )}
                </>
              ) : evaluation ? (
                <>
                  <div className="feedback-icon">
                    {evaluation.error_type === 'correct'
                      ? (streak >= 3 ? '🔥 On Fire!' : '🌟 Amazing!')
                      : '💪 Keep Trying!'}
                  </div>
                  {evaluation.error_type === 'correct' && streak >= 2 && (
                    <div style={{ fontSize: '0.95rem', color: '#81C784', marginBottom: '6px' }}>
                      🔥 {streak} in a row!
                    </div>
                  )}
                  {evaluation.feedback && (
                    <div className="feedback-message" style={{
                      marginTop: '10px',
                      fontSize: '1.2rem',
                      color: evaluation.error_type === 'correct' ? '#81C784' : '#EF9A9A',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      padding: '8px 15px',
                      borderRadius: '15px'
                    }}>
                      {evaluation.feedback}
                    </div>
                  )}
                  {evaluation.error_type !== 'correct' && (
                    <div className="feedback-tip">You said: "{userSpeech}"</div>
                  )}
                  {lastAudioUrl && (
                    <button onClick={replayUserAudio} className="replay-btn">
                      🔁 Replay My Voice
                    </button>
                  )}
                </>
              ) : (
                <div className="thinking">🤔 Listening...</div>
              )}
            </div>
          )}
        </div>

        {/* Navigation - Right */}
        <button
          className="nav-arrow next"
          onClick={handleNextExercise}
          disabled={currentExerciseIndex >= exerciseIds.length - 1 && !evaluation}
        >
          ➡️
        </button>
      </div>
    </div>
  );
}